import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Stack,
  Text,
  Group,
  Grid,
  Select,
  NumberInput,
  ActionIcon,
  Button,
  Alert,
  Chip,
} from '@mantine/core';
import { IconPlus, IconTrash, IconAlertCircle } from '@tabler/icons-react';
import { ChargeItemDefinition, ChargeItem } from '@medplum/fhirtypes';
import { PaymentSource } from '../../lims-types';
import {
  DEFAULT_CURRENCY,
  DEFAULT_PAYMENT_SOURCE,
} from '../../constants/payment.consts';
import {
  PricingOption,
  getPaymentSource,
  getDiscount,
} from '../../utils/payment-helpers';

export interface SplitItem {
  source: PaymentSource | '';
  amount: number;
  discount: number;
}

export interface PaymentModalProps {
  opened: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  chargeDefinition?: ChargeItemDefinition;
  pricingOptions: PricingOption[];
  sourceOptions: { value: string; label: string }[];
  mainChargeItems?: ChargeItem[];
  paymentSource?: PaymentSource;
  totalGross?: number;
  onSave: (
    splits: { source: PaymentSource; amount: number; discount?: number }[]
  ) => Promise<void>;
}

const DEFAULT_SPLITS: SplitItem[] = [
  { source: DEFAULT_PAYMENT_SOURCE, amount: 0, discount: 0 },
];

export function PaymentModal({
  opened,
  onClose,
  mode,
  chargeDefinition,
  pricingOptions,
  sourceOptions,
  mainChargeItems,
  paymentSource,
  totalGross,
  onSave,
}: PaymentModalProps) {
  const { t } = useTranslation('lims');

  const title =
    mode === 'create' ? t('payment.createPayment') : t('payment.editPayment');
  const saveButtonLabel =
    mode === 'create' ? t('payment.create') : t('payment.saveChanges');

  const initialSplits: SplitItem[] = useMemo(() => {
    if (mode === 'create') {
      return DEFAULT_SPLITS;
    }
    if (mainChargeItems && mainChargeItems.length > 0) {
      return mainChargeItems.map((ci) => {
        const discount = getDiscount({ extension: ci.extension });
        return {
          source:
            getPaymentSource({ extension: ci.extension } as any) ||
            DEFAULT_PAYMENT_SOURCE,
          amount: (ci.priceOverride?.value || 0) + discount,
          discount,
        };
      });
    }
    return [
      { source: paymentSource || '', amount: totalGross || 0, discount: 0 },
    ];
  }, [mode, mainChargeItems, paymentSource, totalGross]);

  const [form, setForm] = useState({
    splits: initialSplits,
  });
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showSplits, setShowSplits] = useState(false);

  useEffect(() => {
    if (opened) {
      setForm({ splits: initialSplits });
      setLocalError(null);

      if (mode === 'create' && pricingOptions && pricingOptions.length > 0) {
        setShowSplits(false);
      } else {
        setShowSplits(true);
      }
    }
  }, [opened, initialSplits, mode, pricingOptions]);

  const total = form.splits.reduce(
    (sum, s) => sum + Math.max(0, s.amount - (s.discount || 0)),
    0
  );

  const handleSave = async () => {
    const hasMissingSource = form.splits.some((s) => !s.source);
    if (hasMissingSource) {
      setLocalError(
        t('payment.errors.invalidSplits', 'Please enter a valid source.')
      );
      return;
    }

    const validSplits = form.splits.filter((s) => s.amount >= 0);
    if (validSplits.length === 0) {
      setLocalError(
        t('payment.errors.invalidSplits', 'Please enter a valid source.')
      );
      return;
    }
    const hasInvalidDiscount = form.splits.some(
      (s) => (s.discount || 0) > s.amount
    );
    if (hasInvalidDiscount) {
      setLocalError(
        t(
          'payment.errors.invalidDiscount',
          'Discount cannot exceed the amount.'
        )
      );
      return;
    }
    try {
      setSaving(true);
      setLocalError(null);
      await onSave(
        validSplits.map((s) => ({
          source: s.source as PaymentSource,
          amount: s.amount,
          discount: s.discount,
        }))
      );
      handleClose();
    } catch (e: any) {
      setLocalError(e.message || 'Failed to save payment');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setLocalError(null);
    onClose();
  };

  const handleSplitChange = <K extends keyof SplitItem>(
    index: number,
    field: K,
    value: SplitItem[K]
  ) => {
    setForm((prev) => {
      const newSplits = [...prev.splits];
      newSplits[index] = { ...newSplits[index], [field]: value };
      return { splits: newSplits };
    });
  };

  const handleAddSplit = () => {
    setForm((prev) => ({
      splits: [
        ...prev.splits,
        { source: DEFAULT_PAYMENT_SOURCE, amount: 0, discount: 0 },
      ],
    }));
  };

  const handleRemoveSplit = (index: number) => {
    setForm((prev) => ({
      splits: prev.splits.filter((_, i) => i !== index),
    }));
  };

  const disabled =
    total < 0 ||
    (mode === 'create' && pricingOptions.length > 0 && !showSplits);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={title}
      centered
      size="lg"
    >
      <Stack gap="md">
        {localError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {localError}
          </Alert>
        )}

        {pricingOptions.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              {t('payment.labels.suggestedPrices', {
                title: chargeDefinition?.title,
              })}
            </Text>
            <Group gap="xs">
              {pricingOptions.map((opt, idx) => {
                const isSelected =
                  form.splits.length === opt.splits.length &&
                  opt.splits.every((os) =>
                    form.splits.some(
                      (fs) =>
                        fs.source === os.source &&
                        Math.abs(fs.amount - os.amount) < 0.01 &&
                        Math.abs((fs.discount || 0) - (os.discount || 0)) < 0.01
                    )
                  );
                return (
                  <Chip
                    key={idx}
                    size="md"
                    variant="light"
                    checked={isSelected}
                    onClick={() => {
                      setShowSplits(true);
                      setForm({
                        splits: opt.splits.map((s) => ({
                          source:
                            (s.source as PaymentSource) ||
                            DEFAULT_PAYMENT_SOURCE,
                          amount: s.amount,
                          discount: s.discount || 0,
                        })),
                      });
                    }}
                  >
                    {opt.label}
                  </Chip>
                );
              })}
            </Group>
          </Stack>
        )}

        {showSplits && (
          <>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                {t('payment.labels.splits')}
              </Text>

              <Grid gutter="sm">
                <Grid.Col span={5}>
                  <Text fw={500} size="xs" c="dimmed">
                    {t('payment.labels.source')}
                  </Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Text fw={500} size="xs" c="dimmed">
                    {t('payment.labels.amount')}
                  </Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Text fw={500} size="xs" c="dimmed">
                    {t('payment.labels.discount')}
                  </Text>
                </Grid.Col>
                <Grid.Col span={1} />
              </Grid>

              {form.splits.map((split, index) => (
                <Grid key={index} gutter="sm" align="center">
                  <Grid.Col span={5}>
                    <Select
                      size="md"
                      data={sourceOptions}
                      value={split.source}
                      onChange={(v) =>
                        handleSplitChange(index, 'source', v as PaymentSource)
                      }
                      placeholder={t('payment.placeholders.selectSource')}
                    />
                  </Grid.Col>

                  <Grid.Col span={3}>
                    <NumberInput
                      size="md"
                      min={0}
                      suffix={` ${DEFAULT_CURRENCY}`}
                      value={split.amount}
                      onChange={(v) =>
                        handleSplitChange(index, 'amount', Number(v) || 0)
                      }
                    />
                  </Grid.Col>

                  <Grid.Col span={3}>
                    <NumberInput
                      size="md"
                      min={0}
                      suffix={` ${DEFAULT_CURRENCY}`}
                      value={split.discount || 0}
                      onChange={(v) =>
                        handleSplitChange(index, 'discount', Number(v) || 0)
                      }
                    />
                  </Grid.Col>

                  <Grid.Col span={1}>
                    <ActionIcon
                      size="lg"
                      color="red"
                      variant="subtle"
                      onClick={() => handleRemoveSplit(index)}
                      disabled={form.splits.length === 1}
                    >
                      <IconTrash size={20} />
                    </ActionIcon>
                  </Grid.Col>
                </Grid>
              ))}
            </Stack>

            <Button
              variant="default"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={handleAddSplit}
            >
              {t('payment.labels.addSplit')}
            </Button>
          </>
        )}

        <Group justify="space-between">
          <Text fw={600}>{t('payment.labels.total')}</Text>
          <Text fw={600}>
            {total.toFixed(2)} {DEFAULT_CURRENCY}
          </Text>
        </Group>

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClose}>
            {t('payment.cancel')}
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={disabled}>
            {saveButtonLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
